/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Snackbar,
} from "@mui/material";
import { ArrowBack, CheckCircle, WarningAmber } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import stripeLogo from "assets/stripeandrazorpaylogo/Stripe_Logo,_revised_2016.svg.webp";
import razorpayLogo from "assets/stripeandrazorpaylogo/Razorpay_logo.webp";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const STRIPE_BLUE = "#635BFF";
const RAZORPAY_BLUE = "#0C2452";

function StatusChip({ enabled, configured }) {
  if (enabled && configured) {
    return <Chip icon={<CheckCircle />} label="Connected" color="success" size="small" />;
  }
  if (configured) {
    return <Chip icon={<WarningAmber />} label="Configured · Disabled" color="warning" size="small" />;
  }
  return <Chip label="Not connected" size="small" variant="outlined" />;
}

export default function PaymentSettingsPage() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const location = useLocation();
  const { axiosInstance } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [toast, setToast] = useState("");

  useEffect(() => {
    const msg = location.state?.paymentGatewayMessage;
    if (msg) {
      setToast(msg);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axiosInstance.get("/payment-gateways/settings");
        if (!cancelled) setSettings(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || err?.message || "Unable to load payment settings."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [axiosInstance]);

  const stripeConfigured = Boolean(settings?.stripe?.configured);
  const stripeEnabled = Boolean(settings?.stripe?.enabled);
  const stripeButtonLabel = stripeConfigured
    ? stripeEnabled
      ? "Manage Stripe"
      : "Enable Stripe"
    : "Connect Stripe";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 3 : 4} pb={6} px={isV2 ? 0 : 2}>
        {isV2 ? (
          <PageHeader title="Payments" summary="Connect your own Stripe or Razorpay account for travel order checkout" />
        ) : (
        <MDBox display="flex" alignItems="center" gap={1} mb={3}>
          <IconButton onClick={() => navigate("/settings")}>
            <ArrowBack />
          </IconButton>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              Payments
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Connect your own Stripe or Razorpay account for travel order checkout
            </MDTypography>
          </MDBox>
        </MDBox>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === "string" ? error : JSON.stringify(error)}
          </Alert>
        )}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #E6E8F0",
                }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                  <MDBox
                    component="img"
                    src={stripeLogo}
                    alt="Stripe"
                    sx={{
                      height: 36,
                      width: "auto",
                      maxWidth: 140,
                      objectFit: "contain",
                    }}
                  />
                  <StatusChip
                    enabled={settings?.stripe?.enabled}
                    configured={settings?.stripe?.configured}
                  />
                </MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="medium"
                  mb={1}
                  sx={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
                >
                  Stripe
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={3} sx={{ flexGrow: 1 }}>
                  Accept card payments with your Stripe account. You will add your API keys and
                  configure the webhook in Stripe.
                </MDTypography>
                <Button
                  fullWidth
                  disableElevation
                  onClick={() => navigate("/settings/payments/stripe")}
                  sx={{
                    backgroundColor: STRIPE_BLUE,
                    color: "#fff !important",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    borderRadius: "6px",
                    py: 1.25,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#4F46E5",
                      color: "#fff !important",
                      boxShadow: "none",
                    },
                  }}
                >
                  {stripeButtonLabel}
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #E6E8F0",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "not-allowed",
                }}
              >
                <MDBox
                  sx={{
                    filter: "blur(5px)",
                    opacity: 0.5,
                    pointerEvents: "none",
                    userSelect: "none",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                    <MDBox
                      component="img"
                      src={razorpayLogo}
                      alt="Razorpay"
                      sx={{
                        height: 52,
                        width: "auto",
                        maxWidth: 220,
                        objectFit: "contain",
                      }}
                    />
                    <StatusChip
                      enabled={settings?.razorpay?.enabled}
                      configured={settings?.razorpay?.configured}
                    />
                  </MDBox>
                  <MDTypography
                    variant="h5"
                    fontWeight="medium"
                    mb={1}
                    sx={{ fontFamily: '"Inter", "Segoe UI", Helvetica, Arial, sans-serif' }}
                  >
                    Razorpay
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={3} sx={{ flexGrow: 1 }}>
                    Accept UPI and card payments with your Razorpay account. You will add your keys
                    and configure the webhook in Razorpay.
                  </MDTypography>
                  <Button
                    fullWidth
                    disableElevation
                    disabled
                    tabIndex={-1}
                    sx={{
                      backgroundColor: RAZORPAY_BLUE,
                      color: "#fff !important",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      fontFamily: '"Inter", "Segoe UI", Helvetica, Arial, sans-serif',
                      borderRadius: "6px",
                      py: 1.25,
                      boxShadow: "none",
                    }}
                  >
                    Set up Razorpay
                  </Button>
                </MDBox>

                <MDBox
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      bgcolor: "rgba(255, 255, 255, 0.45)",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                >
                  <MDBox
                      component="img"
                      src={razorpayLogo}
                      alt="Razorpay"
                      sx={{
                        height: 90,
                        width: "auto",
                        maxWidth: 320,
                        objectFit: "contain",
                        filter: "drop-shadow(0 1px 6px rgba(255,255,255,0.9))",
                      }}
                  />
                  <MDTypography
                      variant="h3"
                      fontWeight="bold"
                      sx={{
                        color: RAZORPAY_BLUE,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        textShadow: "0 1px 12px rgba(255,255,255,0.9)",
                      }}
                  >
                    Coming Soon
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setToast("")} sx={{ width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
